import React, { useState } from 'react';
import styled from 'styled-components/macro';
import { translations } from '../../../../locales/translations';
import { DisplayFieldHorizontal } from './DisplayFieldHorizontal';
import { useSelector } from 'react-redux';
import { selectBiDetail, selectCurrency, selectOneOffView, selectPreviewMode } from '../slice/selectors';
import { DisplayFieldVertical } from './DisplayFieldVertical';
import { QuoteItemTable } from './QuoteItemTable';
import { Box, Grid } from '@mui/material';
import { biType } from '../../../../locales/types';
import { selectContractStatus, selectWorklistData } from '../../WorkListPage/slice/selectors';

export function QuoteItemCard(props) {
  const { data, displayType, viewChangeData, isNewItem } = props;
  const currency = useSelector(selectCurrency);
  const previewMode = useSelector(selectPreviewMode);
  const [displayData, setDisplayData] = useState(data);
  const [highlightChange, setHighlightChange] = useState(false);

  // assume non-mrc products are yrc products, to cover the case some product are 0 mrc, 0 yrc
  const getMrcProducts = data => { return data?.products?.filter(prod => prod.mrc) ?? [] }
  const getYrcProducts = data => { return data?.products?.filter(prod => !prod.mrc) ?? [] }
  const hasMrc = Boolean(getMrcProducts(data).length || getMrcProducts(viewChangeData).length);
  const hasYrc = Boolean(getYrcProducts(data).length || getYrcProducts(viewChangeData).length);
  const removeTerminationDate = (products) => products?.map(prod => {
    return {
      ...prod,
      terminationDate: undefined,
    }
  })

  const contractStatus = useSelector(selectContractStatus);

  React.useEffect(() => {
    if (previewMode && viewChangeData) {
      setHighlightChange(true);
      setDisplayData(viewChangeData);
    } else {
      setDisplayData(data);
      setHighlightChange(false);
    }

    if(contractStatus![`${displayType}Status`] === 'Auto'){
      setHighlightChange(false);
    }

  }, [previewMode, data]);

  return (
    <Wrapper>
      <div className={`card ${isNewItem && !!highlightChange && 'new-item-highlight'}`}>
        <div className='row'>
          <div className='location-header'>{data.location}</div>
          <Grid container className='other-header'>
            <Grid item md={3} lg>
              <DisplayFieldHorizontal
                label={translations.contractDetail.startDate}
                value={displayData.startDate}
                displayChange={highlightChange && displayData.startDate !== data.startDate}
              />
            </Grid>
            <Grid item md={3} lg>
              <DisplayFieldHorizontal
                label={translations.contractDetail.endDate}
                value={displayData.endDate}
                displayChange={highlightChange && displayData.endDate !== data.endDate}
              />
            </Grid>
            {displayType === biType.RECURRING && hasMrc && (
              <Grid item md={3} lg>
                <DisplayFieldHorizontal
                  label={translations.contractDetail.MRCCharge}
                  value={
                    currency + ' ' +
                    displayData.mrc.toLocaleString(undefined, {
                      maximumFractionDigits: 2,
                    })
                  }
                  displayChange={highlightChange && displayData.mrc !== data.mrc}
                />
              </Grid>
            )}
            {displayType === biType.RECURRING && hasYrc && (
              <Grid item md={3} lg>
                <DisplayFieldHorizontal
                  label={translations.contractDetail.YRCCharge}
                  value={
                    currency + ' ' +
                    displayData.yrc.toLocaleString(undefined, {
                      maximumFractionDigits: 2,
                    })
                  }
                  displayChange={highlightChange && displayData.yrc !== data.yrc}
                />
              </Grid>
            )}
            {displayType === biType.ONEOFF && (
              <Grid item md={3} lg>
                <DisplayFieldHorizontal
                  className='horizontal-item'
                  label={translations.biType.oneOff}
                  value={`${currency} ${displayData.nrc.toLocaleString(undefined, { maximumFractionDigits: 2 })}`}
                  displayChange={highlightChange && displayData.nrc !== data.nrc}
                />
              </Grid>
            )}
            <Grid item md={3} lg>
              <DisplayFieldHorizontal
                label={translations.contractDetail.poNumber}
                value={displayData.poNumber}
                displayChange={highlightChange && displayData.poNumber !== data.poNumber}
              />
            </Grid>
          </Grid>
        </div>
        <div className='row'>
          <DisplayFieldVertical
            className='quote-item'
            label={translations.contractDetail.quoteItem}
            value={displayData.description}
            displayChange={highlightChange && displayData.description !== data.description}
          />
        </div>
        {
          displayType === biType.RECURRING && hasMrc &&
          <QuoteItemTable
            productData={getMrcProducts(displayData)}
            originalData={getMrcProducts(data)}
            displayType={displayType}
            displayChange={highlightChange}
            isMrc={true}
          />
        }
        {
          displayType === biType.RECURRING && hasYrc &&
          <>
          <Box mb={1} />
          <QuoteItemTable
            productData={getYrcProducts(displayData)}
            originalData={getYrcProducts(data)}
            displayType={displayType}
            displayChange={highlightChange}
            isMrc={false}
          />
          </>
        }
        {
          displayType === biType.ONEOFF &&
          <QuoteItemTable
            productData={removeTerminationDate(displayData.products)}
            originalData={removeTerminationDate(data.products)}
            displayType={displayType}
            displayChange={highlightChange}
          />
        }

      </div>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  .card {
    border: groove 2px;
    border-radius: 5px;
    padding: 5px;
    margin: 5px 0;
  }

  .new-item-highlight {
    background-color: ${p => p.theme.pending};
  }

  .row {
    display: flex;
    align-items: center;
    width: 100%;
    margin-bottom: 5px;

    .location-header {
      width: 7%;
    }

    .other-header {
      display: flex;
      width: 93%;
    }
  }

  .quote-item {
    margin-bottom: 5px;
  }
`;
